import { useCallback, useEffect, useRef, useState } from "react";
import { ref, update, serverTimestamp } from "firebase/database";
import { v4 as uuidv4 } from "uuid";
import { rtdb } from "@/lib/firebase";
import type { ParsedQr } from "@/lib/qrParse";

const ID_KEY = "qr-pwa.participantId";
const NICKNAME_KEY = "qr-pwa.participantNickname";

const HEARTBEAT_MS = 30_000;
const NICKNAME_MIN = 2;
const NICKNAME_MAX = 15;

export interface ParticipantState {
  id: string;
  nickname: string | null;
  /** Persists locally (sessionStorage) and in the RTDB participants registry. */
  setNickname: (name: string) => Promise<void>;
}

const validate = (name: string): string | null => {
  const trimmed = name.trim();
  if (trimmed.length < NICKNAME_MIN || trimmed.length > NICKNAME_MAX) return null;
  return trimmed;
};

/**
 * Returns a stable participantId for this browser session, and a `nickname`
 * the user picks at first load. Pushes a heartbeat to RTDB every 30s while
 * the page is visible.
 */
export const useParticipant = (parsed: ParsedQr | null): ParticipantState => {
  const [id] = useState<string>(() => {
    let v = sessionStorage.getItem(ID_KEY);
    if (!v) {
      v = uuidv4();
      sessionStorage.setItem(ID_KEY, v);
    }
    return v;
  });
  const [nickname, setNicknameState] = useState<string | null>(() =>
    sessionStorage.getItem(NICKNAME_KEY)
  );

  const intervalRef = useRef<number | null>(null);
  const nicknameRef = useRef(nickname);
  nicknameRef.current = nickname;

  useEffect(() => {
    if (!parsed) return;
    const heartbeat = () => {
      if (document.visibilityState === "hidden") return;
      const pRef = ref(rtdb, `${parsed.restoId}/${parsed.qrId}/participants/${id}`);
      const payload: Record<string, unknown> = {
        lastSeenAt: serverTimestamp(),
        source: "phone",
      };
      if (nicknameRef.current) payload.nickname = nicknameRef.current;
      // update() merges fields → preserves nickname even if heartbeat omits it.
      update(pRef, payload).catch(() => {});
    };
    heartbeat();
    intervalRef.current = window.setInterval(heartbeat, HEARTBEAT_MS);
    document.addEventListener("visibilitychange", heartbeat);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener("visibilitychange", heartbeat);
    };
  }, [parsed?.restoId, parsed?.qrId, id]);

  const setNickname = useCallback(
    async (name: string): Promise<void> => {
      const valid = validate(name);
      if (!valid) return;
      sessionStorage.setItem(NICKNAME_KEY, valid);
      setNicknameState(valid);
      if (!parsed) return;
      const pRef = ref(rtdb, `${parsed.restoId}/${parsed.qrId}/participants/${id}`);
      try {
        await update(pRef, { nickname: valid });
      } catch (err) {
        console.warn("[useParticipant.setNickname] RTDB write failed", err);
      }
    },
    [parsed?.restoId, parsed?.qrId, id]
  );

  return { id, nickname, setNickname };
};
